// ABOUTME: Reconciles confirmed job associations into an exact candidate profile cache.
// ABOUTME: Prevents stale profile requests from replacing mutation-confirmed job data.
import { QueryClient, QueryKey } from "react-query";

const associationRetryDelaysMs = [0, 500, 1000, 2000];

const normalizeJobId = (jobId: string) => jobId.replace(/^JOB:/, "");

const profileHasJob = (
    applicantProfile: Compleo.IObject | undefined,
    expectedJobId: string
) =>
    Array.isArray(applicantProfile?.data?.Jobs) &&
    applicantProfile.data.Jobs.some(
        (job: Compleo.IObject) =>
            normalizeJobId(String(job.JobId)) === normalizeJobId(expectedJobId)
    );

export const refreshApplicantProfileAfterJobAssociation = async ({
    queryClient,
    queryKey,
    applicantJob,
    expectedJobId,
    retryDelaysMs = associationRetryDelaysMs,
    shouldContinue = () => true
}: {
    queryClient: QueryClient;
    queryKey: QueryKey;
    applicantJob?: Compleo.IObject;
    expectedJobId?: string;
    retryDelaysMs?: number[];
    shouldContinue?: () => boolean;
}) => {
    if (!shouldContinue()) {
        return;
    }
    if (!applicantJob) {
        await queryClient.cancelQueries(
            queryKey,
            { exact: true },
            { revert: false }
        );
        if (!expectedJobId) {
            await queryClient.invalidateQueries(queryKey, {
                exact: true
            });
            return;
        }

        for (const delayMs of retryDelaysMs) {
            if (!shouldContinue()) {
                return;
            }
            if (delayMs > 0) {
                await new Promise((resolve) => setTimeout(resolve, delayMs));
            }
            if (!shouldContinue()) {
                return;
            }
            await queryClient.refetchQueries(queryKey, {
                exact: true,
                active: true,
                inactive: true
            });
            if (!shouldContinue()) {
                return;
            }
        }
        if (
            queryClient.getQueryState(queryKey)?.status === "success" &&
            profileHasJob(
                queryClient.getQueryData<Compleo.IObject>(queryKey),
                expectedJobId
            )
        ) {
            return;
        }
        await queryClient.invalidateQueries(queryKey, {
            exact: true,
            refetchActive: false
        });
        return;
    }

    await queryClient.cancelQueries(
        queryKey,
        { exact: true },
        { revert: false }
    );
    queryClient.setQueryData<Compleo.IObject>(queryKey, (applicantProfile) => {
        if (!applicantProfile?.data) {
            return applicantProfile;
        }

        const jobs = Array.isArray(applicantProfile.data.Jobs)
            ? applicantProfile.data.Jobs
            : [];
        const existingJobIndex = jobs.findIndex(
            (job: Compleo.IObject) => job.JobId === applicantJob.JobId
        );
        const updatedJobs =
            existingJobIndex >= 0
                ? jobs.map((job: Compleo.IObject, index: number) =>
                      index === existingJobIndex
                          ? { ...job, ...applicantJob }
                          : job
                  )
                : [...jobs, applicantJob];

        return {
            ...applicantProfile,
            data: {
                ...applicantProfile.data,
                Jobs: updatedJobs
            }
        };
    });
    await queryClient.invalidateQueries(queryKey, {
        exact: true,
        refetchActive: false
    });
};
